// Утилиты для работы с мобильными устройствами и VK ID
import { MobileCompatibilityCheck } from '../types';

export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const isAndroidDevice = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

export const isBraveNewsBrowser = (): boolean => {
  return (navigator as any).brave !== undefined;
};

export const isStandaloneApp = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches;
};

export const getViewportHeight = (): number => {
  // Возвращает высоту viewport с учетом виртуальной клавиатуры
  return window.visualViewport?.height || window.innerHeight;
};

export const detectBrowserIssues = (): string[] => {
  const issues: string[] = [];
  
  if (isBraveNewsBrowser()) {
    issues.push('Brave Browser может вызывать проблемы с VK ID. Попробуйте отключить "Prevent sites from fingerprinting me based on my language preferences" в настройках.');
  }
  
  if (isIOSDevice() && !window.location.protocol.startsWith('https')) {
    issues.push('iOS требует HTTPS для корректной работы VK ID.');
  }
  
  // Проверяем поддержку WebCrypto API
  if (!crypto || !crypto.getRandomValues) {
    issues.push('Браузер не поддерживает WebCrypto API, что может вызвать проблемы с безопасностью.');
  }
  
  return issues;
};

export const waitForViewportStabilization = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!window.visualViewport) {
      // Если visualViewport не поддерживается, просто ждем 100ms
      setTimeout(resolve, 100);
      return;
    }
    
    let resizeTimeout: number;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        window.visualViewport?.removeEventListener('resize', handleResize);
        resolve();
      }, 100);
    };
    
    window.visualViewport.addEventListener('resize', handleResize);
    
    // Timeout через 5 секунд на случай, если resize не произойдет
    setTimeout(() => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      resolve();
    }, 5000);
  });
};

// Функция для проверки доступности VK ID на мобильных устройствах
export const checkVKIDCompatibility = (): MobileCompatibilityCheck => {
  const issues: string[] = [];
  
  // Проверяем основные требования
  if (!window.crypto || !window.crypto.getRandomValues) {
    issues.push('WebCrypto API не поддерживается');
  }
  
  if (!window.sessionStorage) {
    issues.push('SessionStorage не поддерживается');
  }
  
  if (isMobileDevice() && !window.location.protocol.startsWith('https')) {
    issues.push('HTTPS обязателен для мобильных устройств');
  }
  
  // Специфичные проблемы для браузеров
  const browserIssues = detectBrowserIssues();
  issues.push(...browserIssues);
  
  return {
    compatible: issues.length === 0,
    issues
  };
};

// Функция для оптимизации работы с виртуальной клавиатурой
export const handleVirtualKeyboard = (callback: (visible: boolean) => void): (() => void) => {
  if (!window.visualViewport) {
    return () => {}; // Возвращаем пустую функцию очистки
  }
  
  const initialHeight = window.visualViewport.height;
  let keyboardVisible = false;
  
  const handleResize = () => {
    const currentHeight = window.visualViewport!.height;
    const heightDifference = initialHeight - currentHeight;
    const isKeyboardVisible = heightDifference > 150; // Threshold для виртуальной клавиатуры
    
    if (isKeyboardVisible !== keyboardVisible) {
      keyboardVisible = isKeyboardVisible;
      callback(keyboardVisible);
    }
  };
  
  window.visualViewport.addEventListener('resize', handleResize);
  
  // Возвращаем функцию очистки
  return () => {
    window.visualViewport?.removeEventListener('resize', handleResize);
  };
}; 

export const detectMobileUserAgent = (): string | null => {
  if (navigator.userAgent.includes("iPhone") && typeof (window as any)?.TelegramWebviewProxy !== 'undefined' && typeof (window as any)?.TelegramWebviewProxyProto !== 'undefined') {
    return 'Found Telegram Webview IOS';
  }

  if (navigator.userAgent.includes("Android") && typeof (window as any)?.TelegramWebview !== 'undefined') {
    return 'Found Telegram Webview Android';
  }

  return null;
};

export const isMobileUserAgent = (): boolean => {
  return detectMobileUserAgent() !== null;
}; 